import { PartInKit } from '@components/Product/Shared/PartsTab/PartInKit';
import type { AccountDBSource, PartNode } from '@partful/shared-types';
import { useKit } from '@services/kits/kit';
import { useEffect } from 'react';
import { EmptyTab } from 'src/components/EmptyTab';
import { Autocomplete } from 'src/components/Product/Desktop/Panels/PartsCatalogue/Autocomplete';
import { SidebarNavigation } from 'src/components/Product/Desktop/Panels/PartsCatalogue/SidebarNavigation';
import { SearchStyles, ThinDivider } from 'src/components/Product/Desktop/Panels/PartsCatalogue/styles';
import TabHeader from 'src/components/Product/Desktop/Panels/PartsCatalogue/TabHeader';
import { TabPanelElement } from 'src/components/Product/Shared/TabPanelElement';
import { useQueryString } from 'src/hooks/useQueryString';
import { useTenant } from 'src/providers/Tenant';
import { GA } from 'src/services/gtag';
import { usePart } from 'src/services/part';
import { PartNavigation, usePartNavigation, type NamedLink } from 'src/services/partNavigation';
import { usePartsWithPriceOrWithChildren } from 'src/services/usePartsWithPriceOrWithChildren';
import { BuyPartsSection } from './BuyPartSection/BuyPartSection';
import { PartChildren } from './PartChildren';
import { PartDetails } from './PartDetails';
import { PartHistory } from './PartHistory';
import { PartLocations } from './PartLocations';
import { PartOptions } from './PartOptions';

function sendGaEvent(
  partNavigation: PartNavigation,
  tenant: AccountDBSource,
  currentPart: PartNode | undefined,
  defaultBuyableQuantity: string | undefined
) {
  if (partNavigation.children.length) {
    // Viewing a list
    const items = partNavigation.children.map((child) => child.data!);
    GA.Ecommerce.viewItemList('Parts Catalogue', 'parts-catalogue', items);
  } else {
    // Viewing a single item
    GA.Ecommerce.viewItem(tenant, [currentPart!], parseInt(defaultBuyableQuantity ?? '1'));
  }
}

export default function PartsCatalogue() {
  const part = usePart();
  const partNavigation = usePartNavigation();
  const { kitNamedLink: kitsToDisplay } = useKit();
  const defaultBuyableQuantity = useQueryString('defaultBuyableQuantity');
  const tenant = useTenant();

  const configManagement = tenant.enabledFeatures.includes('configuration-management');
  const currentPart = part.current ?? part.partPath?.part;
  const partParents = partNavigation.parents;

  const childrenToDisplay = usePartsWithPriceOrWithChildren(
    configManagement ? partNavigation.children : alphabeticallySortedChildren(partNavigation.children)
  );
  const hasAnyChildren = Boolean(partNavigation.children.length);

  useEffect(
    () => sendGaEvent(partNavigation, tenant, currentPart, defaultBuyableQuantity),
    [partNavigation.children, currentPart, partNavigation, tenant, defaultBuyableQuantity]
  );

  if (!currentPart) {
    return <EmptyTab />;
  }

  return (
    <TabPanelElement>
      <SearchStyles>
        <Autocomplete />
      </SearchStyles>
      <ThinDivider />
      {partParents?.length > 0 && <SidebarNavigation partParents={partParents} />}
      <TabHeader currentPart={currentPart} />
      <PartOptions currentPart={currentPart} parents={partParents} key={currentPart.id} />
      <BuyPartsSection currentPart={currentPart} defaultBuyableQuantity={defaultBuyableQuantity} />
      <PartDetails currentPart={currentPart} />
      {hasAnyChildren ? (
        <PartChildren
          childrenToDisplay={childrenToDisplay}
          enabledFeatures={tenant.enabledFeatures}
          kitsToDisplay={kitsToDisplay}
        />
      ) : (
        <>
          <PartHistory currentPart={currentPart} />
          <PartLocations />
          <PartInKit currentPart={currentPart} divider />
        </>
      )}
    </TabPanelElement>
  );
}

const alphabeticallySortedChildren = (children: NamedLink[]): NamedLink[] =>
  [...children].sort((childA, childB) => childA.name.localeCompare(childB.name));
